import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Flex, Form, message, Switch } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { RcFile } from 'antd/lib/upload';

import ThumbnailSelector from '../Thumbnail/ThumbnailSelector';

import {
  ImageEditorProps,
  ImageEditorState,
  PreviewDetails,
  SettingsData,
} from '../../types';
import { defaultImageEditorProps, getSignedUrl } from '../utils';

import styles from './GiideSettings.module.css';
import ImageEditor from '../ImageEditor/ImageEditor';
import AvatarEditor from 'react-avatar-editor';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../graphql/types';
import { setImageEditorState, setUseTemplate } from '../../actions';
import { colors } from '../../theme';
import { LoadingOutlined } from '@ant-design/icons';

interface IProps {
  giideData: SettingsData;
  imageForm: FormInstance;
  updateInput: (key: string, value: string | boolean) => void;
  updateColor: (cardColor: string) => void;
  updateShade: (cardShade: string) => void;
  updateThumbnail: (thumbnail: string) => void;
  uploadThumbnail: (
    file: RcFile,
    returnData?: boolean
  ) => Promise<boolean> | Promise<{}>;
  removeThumbnail: () => void;
  updateThumbnailData: (file: File) => void;
  selectCustomThumbnail: () => void;
  editorRef: AvatarEditor | null;
  setEditorRef: (editor: AvatarEditor | null) => void;
  saving?: boolean;
  setLoading: (imgLoading: boolean) => void;
  loading: boolean;
  setGiideData: (giide: SettingsData) => void;
}

const ImageSettings: React.FC<IProps> = ({
  giideData,
  imageForm,
  updateInput,
  updateColor,
  updateShade,
  updateThumbnail,
  updateThumbnailData,
  uploadThumbnail,
  removeThumbnail,
  selectCustomThumbnail,
  editorRef,
  setEditorRef,
  saving = false,
  setLoading,
  loading,
  setGiideData,
}) => {
  const dispatch = useDispatch();
  const [thumbnailImg, setThumbnailImg] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const previewDetails = useSelector<IState, PreviewDetails>(
    state => state.previewDetails ?? { loaded: false, loading: false }
  );

  const useTemplate = useSelector<IState, boolean>(
    state =>
      state.useTemplate ??
      !!(
        !state.giide?.cardDetails?.useCustomThumbnail &&
        state.giide?.cardDetails?.thumbnailImg
      )
  );
  const newImgLoadedBlob = useSelector<IState, string | undefined>(
    state => state.newImgLoadedBlob
  );
  const imageEditorState = useSelector<IState, ImageEditorState | undefined>(
    state => state.imageEditorState
  );

  const handleLoadingImg = useCallback(async () => {
    try {
      setLoading(true);

      let prefix = 'public';

      if (giideData.cardDetails.thumbnailImageService === 'cloudinary') {
        prefix = 'studio';
      }

      const signedUrl = await getSignedUrl(
        `${prefix}/${giideData.cardDetails.thumbnailCustomPath}`,
        undefined,
        undefined,
        undefined,
        giideData.cardDetails.thumbnailImageService
      );

      const data = await fetch(signedUrl?.url as string);

      if (data.ok) {
        const blob = await data.blob();
        setThumbnailImg(URL.createObjectURL(blob));
      }

      setLoading(false);
    } catch (_) {
      message.error('Error occurred while loading image.');
    }
  }, [
    giideData.cardDetails.thumbnailCustomPath,
    giideData.cardDetails.thumbnailImageService,
    setLoading,
  ]);

  useEffect(() => {
    if (!thumbnailImg && newImgLoadedBlob && imageEditorState) {
      setThumbnailImg(newImgLoadedBlob);
      // uncomment this if you want to static image instead of image in editor
      // after going back in publish flow
      // dispatch(setNewImgLoadedBlob(undefined));
    } else if (
      !thumbnailImg &&
      giideData.cardDetails.thumbnailCustomPath &&
      !newImgLoadedBlob
    ) {
      handleLoadingImg();
    }
  }, [
    dispatch,
    newImgLoadedBlob,
    handleLoadingImg,
    thumbnailImg,
    giideData.cardDetails.thumbnailCustomPath,
    imageEditorState,
  ]);

  const handleUpload = useCallback(
    async (file: RcFile) => {
      if (file) {
        setUploading(true);
        setThumbnailImg(URL.createObjectURL(file));
        const uploaded = await uploadThumbnail(file);
        if (!uploaded) {
          setThumbnailImg(null);
        }
      }
      setUploading(false);
      return Promise.reject();
    },
    [setThumbnailImg, uploadThumbnail]
  );

  const handleUpdateThumbnail = useCallback(
    async file => {
      setThumbnailImg(URL.createObjectURL(file));
      await updateThumbnail(file.name);
    },
    [updateThumbnail]
  );

  const handleRemove = useCallback(() => {
    setThumbnailImg(null);
    removeThumbnail();
    dispatch(setImageEditorState(undefined));
  }, [dispatch, setThumbnailImg, removeThumbnail]);

  const handleLoadImage = useCallback(
    async file => {
      setThumbnailImg(URL.createObjectURL(file));
      updateThumbnailData(file);
    },
    [updateThumbnailData]
  );

  const imageEditorProps = useMemo<ImageEditorProps>(
    () => ({
      ...defaultImageEditorProps,
      image: thumbnailImg,
      type: 'thumbnail',
      fileName: giideData.cardDetails.thumbnailCustomName || 'unnamed_file',
      handleRemove,
      handleUpload,
      handleLoadImage,
      editorRef,
      setEditorRef,
      handleUpdateThumbnail,
      uploading,
      imgWidth: 165,
      imgHeight: 165,
      imgBorderWidth: 139,
      imgBorderHeight: 56.75,
      sliderWidth: 200.75,
      removeTextClass: styles.removeThumbnailText,
      boxSizeClass: styles.imgEditorBoxSize,
      staticImageClass: styles.staticImage,
      // Handle case where there's a custom image there but it hasn't loaded yet
      // This prevents uploading something else before it's loaded
      loading: !!giideData.cardDetails.thumbnailCustomPath && !thumbnailImg,
      disabled: saving || loading,
      minWidth: 480,
      minHeight: 480,
    }),
    [
      giideData,
      handleLoadImage,
      handleUpdateThumbnail,
      handleRemove,
      handleUpload,
      editorRef,
      setEditorRef,
      thumbnailImg,
      uploading,
      saving,
      loading,
    ]
  );

  return (
    <Form
      layout="vertical"
      requiredMark={false}
      autoComplete="off"
      form={imageForm}
    >
      <div>
        {saving || loading || previewDetails.loading ? (
          <div className={styles.loadingPreviewWrapper}>
            <LoadingOutlined spin className={styles.loadingPreviewIcon} />
          </div>
        ) : (
          <div style={{ height: 350 }}>
            {useTemplate ? (
              <ThumbnailSelector
                color={giideData.cardDetails.cardColor || ''}
                shade={giideData.cardDetails.cardShade || ''}
                selectedThumbnail={giideData.cardDetails.thumbnailImg || ''}
                setThumbnail={updateThumbnail}
                thumbnailImg={thumbnailImg}
                useTemplate={useTemplate}
                selectCustomThumbnail={selectCustomThumbnail}
              />
            ) : (
              <div style={{ width: 280, margin: 'auto' }}>
                <ImageEditor props={imageEditorProps} />
              </div>
            )}
            <Flex
              vertical={true}
              gap={'small'}
              style={{ margin: '0 12px 0' }}
              justify={'flex-start'}
              align={'center'}
            >
              <Flex
                align={'center'}
                gap={'middle'}
                style={{
                  color: colors.black3,
                  width: 'fit-content',
                  marginTop: '16px',
                }}
              >
                Custom Image
                <Switch
                  size="small"
                  onChange={e => dispatch(setUseTemplate(e.valueOf()))}
                  defaultChecked={useTemplate}
                  disabled={uploading || saving || loading}
                />
                Template
              </Flex>
              <div style={{ textAlign: 'left' }}>
                This image will be used as the cover image for your giide, and
                the default image displayed if you share on social media.
              </div>
            </Flex>
          </div>
        )}
      </div>
    </Form>
  );
};

export default ImageSettings;
